import { Injectable, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { VirtualAssistantService } from 'src/shared/services/api/virtual-assistant.service';
import type { MergedEducationalArtifactConfig } from '../educational-workflow-config.types';
import type { WorkflowChipOption } from '../models/conversation-state.model';
import {
  WORKFLOW_SLOT_GENERATION_PATH,
  WORKFLOW_SLOT_QUICK_AGE,
  WORKFLOW_SLOT_QUICK_THEME,
  type QuickContentResult,
} from '../models/conversation-state.model';
import { AssistantConversationStateService } from './assistant-conversation-state.service';
import {
  formatQuickContentPlainText,
  finalizeAssistantEducationalMessage,
} from '../../voice/utils/assistant-message-finalizer.util';
import { AssistantSlotFillingService, type SlotOptionJson } from './assistant-slot-filling.service';
import { AssistantPayloadBuilderService } from './assistant-payload-builder.service';
import { AssistantWorkflowSlotResolverService } from './assistant-workflow-slot-resolver.service';
import type { WorkflowEnrichmentHint } from '../models/assistant-intent.model';
import { parseEducationalContentResponse } from '../knowledge-setup/educational-content-view-model';
import { logAssistantDebug, logWorkflowError, logWorkflowFallback, logWorkflowTransition } from '../utils/assistant-debug.util';
import { dedupeById } from '../utils/workflow-options.util';

export interface WorkflowTurnResult {
  response: string;
  speak: boolean;
  /** When set, TTS uses this instead of full `response` (structured UI shows detail). */
  speechText?: string;
  followUp$?: Observable<WorkflowTurnResult>;
}

type DomainRow = { id: number; name: string };
type QuestionRow = { id: number; content: string };

@Injectable({
  providedIn: 'root',
})
export class AssistantWorkflowEngineService {
  private readonly va = inject(VirtualAssistantService);
  private readonly slotFill = inject(AssistantSlotFillingService);
  private readonly conv = inject(AssistantConversationStateService);
  private readonly translate = inject(TranslateService);
  private readonly payloadBuilder = inject(AssistantPayloadBuilderService);
  private readonly slotResolver = inject(AssistantWorkflowSlotResolverService);

  private merged: MergedEducationalArtifactConfig | null = null;
  private pendingFollowUp: Observable<WorkflowTurnResult> | null = null;

  readonly lastDomains = signal<DomainRow[]>([]);
  readonly lastQuestions = signal<QuestionRow[]>([]);

  startSession(artifact: string): void {
    if (this.shouldResumeVoiceCustomSession(artifact)) {
      this.ensureHybridSessionLoaded(artifact);
      return;
    }
    this.conv.enterEducationalHybrid(artifact);
    this.lastDomains.set([]);
    this.lastQuestions.set([]);
    /** Keep prior merged until HTTP completes so voice/hybrid transition never reads null slots. */
    this.slotFill
      .loadMerged(artifact)
      .pipe(take(1))
      .subscribe({
        next: merged => {
          if (!merged?.shared) {
            console.error('[workflow] startSession missing merged config', { artifact });
            this.merged = null;
            return;
          }
          this.merged = merged;
          this.loadDomainsThenPresentEntry(merged, artifact);
        },
        error: err => {
          console.error('[workflow] startSession loadMerged failed', { artifact, err });
          this.merged = null;
        },
      });
  }

  /** Voice-first: intent resolved an artifact before hybrid is open. */
  bootstrapFromIntent(artifact: string): Observable<WorkflowTurnResult> {
    if (this.isWarmWorkflowSession(artifact)) {
      logAssistantDebug('ACTIVE_WORKFLOW', { action: 'bootstrap_skipped', artifact });
      return this.resumeActiveWorkflow('');
    }
    logAssistantDebug('ACTIVE_WORKFLOW', { action: 'bootstrap_cold_start', artifact });
    this.conv.beginVoiceWorkflowSession(artifact);
    this.lastDomains.set([]);
    this.lastQuestions.set([]);
    this.merged = null;
    return this.slotFill.loadMerged(artifact).pipe(
      take(1),
      switchMap(merged => {
        this.merged = merged;
        this.presentEntryOrNext();
        this.conv.logActiveWorkflowDebug(this.conv.currentSlotKey());
        return of({
          response: this.buildLastAssistantMessage(),
          speak: true,
        } satisfies WorkflowTurnResult);
      }),
      catchError(() =>
        of({
          response: this.translate.instant('voiceAssistant.workflow.sessionEnded'),
          speak: true,
        })
      )
    );
  }

  /** Continue an in-progress workflow turn without re-entering entry flow. */
  continueWorkflow(text: string, enrichment?: WorkflowEnrichmentHint): WorkflowTurnResult {
    return this.handleUserText(text, enrichment);
  }

  /** Resume same-artifact session without resetting generation path or snapshot. */
  resumeActiveWorkflow(message: string): Observable<WorkflowTurnResult> {
    const artifact = this.conv.sessionArtifact();
    if (!artifact) {
      return of({
        response: this.translate.instant('voiceAssistant.workflow.sessionEnded'),
        speak: true,
      });
    }
    if (this.merged) {
      const turn = message.trim()
        ? this.continueWorkflow(message)
        : this.finishTurnAfterPresent();
      return of(turn);
    }
    return this.slotFill.loadMerged(artifact).pipe(
      take(1),
      switchMap(merged => {
        this.merged = merged;
        const turn = message.trim()
          ? this.continueWorkflow(message)
          : this.finishTurnAfterPresent();
        return of(turn);
      }),
      catchError(() =>
        of({
          response: this.translate.instant('voiceAssistant.workflow.sessionEnded'),
          speak: true,
        })
      )
    );
  }

  private isWarmWorkflowSession(artifact: string): boolean {
    return (
      this.conv.workflowSessionActive() &&
      this.conv.sessionArtifact() === artifact &&
      this.conv.generationPathMode() !== 'unset'
    );
  }

  private shouldResumeVoiceCustomSession(artifact: string): boolean {
    return this.isWarmWorkflowSession(artifact) && this.conv.generationPathMode() === 'custom';
  }

  /** Open hybrid UI without wiping an active voice custom workflow. */
  private ensureHybridSessionLoaded(artifact: string): void {
    this.conv.activeArtifact.set(artifact);
    logAssistantDebug('ACTIVE_WORKFLOW', {
      action: 'ensureHybridSessionLoaded',
      artifact,
      skippedReset: true,
    });
    if (this.merged) return;
    this.slotFill
      .loadMerged(artifact)
      .pipe(take(1))
      .subscribe({
        next: merged => {
          if (!merged?.shared) {
            console.error('[workflow] ensureHybridSessionLoaded missing merged config', {
              artifact,
            });
            return;
          }
          this.merged = merged;
          if (!this.lastDomains().length) {
            this.loadDomainsThenPresentEntry(merged, artifact, { skipPresent: true });
          }
        },
        error: err => {
          console.error('[workflow] ensureHybridSessionLoaded loadMerged failed', { artifact, err });
        },
      });
  }

  private loadDomainsThenPresentEntry(
    merged: MergedEducationalArtifactConfig,
    artifact: string,
    options?: { skipPresent?: boolean }
  ): void {
    const lang =
      typeof window !== 'undefined' ? localStorage.getItem('targetedLang') || 'en' : 'en';
    const assessmentId = Number(
      (merged.shared as { apiConstants?: { assessmentIdForDomains?: number } })?.apiConstants
        ?.assessmentIdForDomains ?? 14
    );
    this.va.getDomains(assessmentId, lang).subscribe({
      next: (res: unknown) => {
        const data = (res as { data?: DomainRow[] })?.data ?? [];
        this.lastDomains.set(Array.isArray(data) ? data : []);
        if (!options?.skipPresent) {
          this.presentEntryOrNext();
        }
      },
      error: () => {
        this.lastDomains.set([]);
        if (!options?.skipPresent) {
          this.presentEntryOrNext();
        }
      },
    });
  }

  endSession(): void {
    this.merged = null;
    this.lastDomains.set([]);
    this.lastQuestions.set([]);
    this.pendingFollowUp = null;
  }

  applyChipSelection(slotKey: string, encodedValue: string): WorkflowTurnResult {
    try {
      if (slotKey === WORKFLOW_SLOT_GENERATION_PATH) {
        return this.applyGenerationPathChip(encodedValue);
      }
      if (this.conv.workflowMode() === 'quick') {
        return this.applyQuickChipSelection(slotKey, encodedValue);
      }
      if (!this.merged) {
        return { response: this.translate.instant('voiceAssistant.workflow.sessionEnded'), speak: true };
      }
      if (!this.isAllowedChipSlot(slotKey)) {
        return this.presentStaleChipTurn(slotKey, encodedValue);
      }
      return this.advanceAfterSlot(slotKey, encodedValue);
    } catch (err) {
      logWorkflowError({
        workflowId: this.conv.sessionArtifact(),
        currentStep: this.conv.currentSlotKey(),
        userInput: encodedValue,
        workflowData: this.conv.snapshot(),
        error: err,
      });
      return {
        response: this.translate.instant('voiceAssistant.workflow.unexpectedError'),
        speak: true,
      };
    }
  }

  handleUserText(text: string, enrichment?: WorkflowEnrichmentHint): WorkflowTurnResult {
    if (enrichment?.pathHint === 'quick' && enrichment.topicHint?.trim()) {
      return this.startQuickFlow(enrichment.topicHint.trim());
    }
    if (enrichment?.pathHint === 'custom' && enrichment.topicHint?.trim()) {
      this.conv.quickPrompt.set(enrichment.topicHint.trim());
      return this.applyGenerationPathChip('custom');
    }
    if (enrichment?.patch && Object.keys(enrichment.patch).length > 0) {
      this.conv.applyOrchestratorPatch(enrichment.patch);
    }

    if (this.conv.workflowMode() === 'quick' || this.conv.generationPathMode() === 'quick') {
      return this.handleQuickUserText(text);
    }

    const current = this.conv.currentSlotKey();
    if (current === WORKFLOW_SLOT_GENERATION_PATH) {
      const encoded = this.slotResolver.resolveWorkflowSlotInput(
        current,
        text,
        this.merged,
        this.lastDomains(),
        this.lastQuestions()
      );
      if (encoded) {
        return this.applyGenerationPathChip(encoded);
      }
      this.logClarifyFallback('generation_path_no_match', text);
      this.conv.presentClarification('voiceAssistant.workflow.clarify');
      return {
        response: this.translate.instant('voiceAssistant.workflow.clarify'),
        speak: true,
      };
    }

    if (!this.merged || this.conv.isSessionTimedOut()) {
      this.conv.presentClarification('voiceAssistant.workflow.sessionTimeout');
      return {
        response: this.translate.instant('voiceAssistant.workflow.sessionTimeout'),
        speak: true,
      };
    }

    const domains = this.lastDomains();
    const questions = this.lastQuestions();
    const snapBefore = JSON.stringify(this.conv.snapshot());

    if (
      current === 'learningFocus' &&
      questions.length === 0 &&
      this.conv.snapshot().topicDomain?.id
    ) {
      const domainId = this.conv.snapshot().topicDomain!.id;
      return {
        response: this.translate.instant('voiceAssistant.workflow.loadingOptions'),
        speak: true,
        followUp$: this.loadQuestionsThenResolve$(domainId, text, snapBefore),
      };
    }

    const slotFirst = this.trySlotFirstResolution(text, current, domains, questions);
    if (slotFirst.handled) {
      return slotFirst.turn!;
    }

    const patch = this.slotFill.extractFromMessage(text, this.merged, domains, questions);

    if (Object.keys(patch).length > 0) {
      this.conv.applyOrchestratorPatch(patch);
      if (patch.topicDomain) {
        const domainId = patch.topicDomain.id;
        this.logDebug('[workflow] slot resolved = topicDomain (extractFromMessage)');
        this.logDebug('[workflow] value =', patch.topicDomain.name);
        this.presentGuidedTurnWithChips('voiceAssistant.workflow.loadingOptions', {}, []);
        return {
          response: this.buildLastAssistantMessage(),
          speak: true,
          followUp$: this.loadQuestionsAndPresentNext$(domainId),
        };
      }
    }

    if (JSON.stringify(this.conv.snapshot()) === snapBefore) {
      return this.presentInvalidSlotClarificationTurn(current);
    }

    this.presentNextSlot();
    return this.finishTurnAfterPresent();
  }

  /**
   * Persist slot value, advance workflow, return turn (with followUp$ when topicDomain needs questions).
   */
  private advanceAfterSlot(slotKey: string, encodedValue: string): WorkflowTurnResult {
    if (!this.applyEncodedValue(slotKey, encodedValue)) {
      return this.presentInvalidSlotClarificationTurn(slotKey);
    }

    const displayValue = this.slotDisplayValue(slotKey, encodedValue);
    this.logDebug(`[workflow] slot resolved = ${slotKey}`);
    this.logDebug(`[workflow] value = ${displayValue}`);
    logWorkflowTransition({
      workflowId: this.conv.sessionArtifact(),
      currentStep: slotKey,
      userInput: encodedValue,
      resolvedOption: displayValue,
      workflowData: this.conv.snapshot(),
    });

    if (slotKey === 'topicDomain') {
      const domainId = this.conv.snapshot().topicDomain?.id;
      if (!domainId) {
        return this.presentInvalidSlotClarificationTurn('topicDomain');
      }
      this.presentGuidedTurnWithChips('voiceAssistant.workflow.loadingOptions', {}, []);
      return {
        response: this.buildLastAssistantMessage(),
        speak: true,
        followUp$: this.loadQuestionsAndPresentNext$(domainId),
      };
    }

    this.presentNextSlot();
    this.logWorkflowProgress();
    return this.finishTurnAfterPresent();
  }

  private trySlotFirstResolution(
    text: string,
    current: string | null,
    domains: DomainRow[],
    questions: QuestionRow[]
  ): { handled: boolean; turn?: WorkflowTurnResult } {
    if (!current || !this.merged) {
      return { handled: false };
    }

    const encoded = this.slotResolver.resolveWorkflowSlotInput(
      current,
      text,
      this.merged,
      domains,
      questions
    );

    if (!encoded) {
      return { handled: false };
    }

    if (current === WORKFLOW_SLOT_GENERATION_PATH) {
      this.logDebug('[workflow] generationPath from utterance', {
        pick: encoded,
        text: text.slice(0, 80),
      });
      return { handled: true, turn: this.applyGenerationPathChip(encoded) };
    }

    return { handled: true, turn: this.advanceAfterSlot(current, encoded) };
  }

  private loadQuestionsThenResolve$(
    domainId: number,
    text: string,
    snapBefore: string
  ): Observable<WorkflowTurnResult> {
    return this.loadQuestionsAndPresentNext$(domainId).pipe(
      switchMap(turn => {
        const questions = this.lastQuestions();
        const current = this.conv.currentSlotKey();
        const slotFirst = this.trySlotFirstResolution(
          text,
          current,
          this.lastDomains(),
          questions
        );
        if (slotFirst.handled && slotFirst.turn) {
          return of(slotFirst.turn);
        }
        if (JSON.stringify(this.conv.snapshot()) === snapBefore) {
          return of(this.presentInvalidSlotClarificationTurn(current ?? 'learningFocus'));
        }
        return of(turn);
      })
    );
  }

  /** Load questions for domain, present next slot, return assistant message for that slot. */
  private loadQuestionsAndPresentNext$(domainId: number): Observable<WorkflowTurnResult> {
    const lang =
      typeof window !== 'undefined' ? localStorage.getItem('targetedLang') || 'en' : 'en';
    return this.va.getQuestionsBySection(domainId, lang).pipe(
      map((res: unknown) => {
        const data = (res as { data?: QuestionRow[] })?.data ?? [];
        this.lastQuestions.set(Array.isArray(data) ? data : []);
        this.presentNextSlot();
        this.logWorkflowProgress();
        return this.finishTurnAfterPresent();
      }),
      catchError(() => {
        this.lastQuestions.set([]);
        this.presentNextSlot();
        return of(this.presentInvalidSlotClarificationTurn('learningFocus'));
      })
    );
  }

  private logWorkflowProgress(): void {
    const snap = this.conv.snapshot();
    const next = this.conv.currentSlotKey();
    this.logDebug('[workflow] snapshot =', {
      topicDomain: snap.topicDomain?.name ?? null,
      learningFocus: snap.learningFocus?.content ?? null,
      pedagogy: snap.pedagogy,
      standardLevel: snap.standardLevel,
      countryRegion: snap.countryRegion,
    });
    this.logDebug('[workflow] nextSlot =', next);
    const key = this.conv.lastGuidedMessageKey();
    if (key) {
      this.logDebug(
        '[workflow] assistantQuestion =',
        this.translate.instant(key, this.conv.lastGuidedMessageParams())
      );
    }
  }

  private slotDisplayValue(slotKey: string, encoded: string): string {
    if (slotKey === 'topicDomain' && encoded.startsWith('id:')) {
      const d = this.lastDomains().find(x => x.id === Number(encoded.slice(3)));
      return d?.name ?? encoded;
    }
    if (slotKey === 'learningFocus' && encoded.startsWith('id:')) {
      const q = this.lastQuestions().find(x => x.id === Number(encoded.slice(3)));
      return String(q?.content ?? encoded);
    }
    return encoded;
  }

  private presentInvalidSlotClarificationTurn(slotKey: string | null): WorkflowTurnResult {
    if (!this.merged?.shared || !slotKey) {
      this.logClarifyFallback(
        !slotKey ? 'missing_current_slot' : 'missing_merged_config',
        undefined,
        null
      );
      this.conv.presentClarification('voiceAssistant.workflow.clarify');
      return {
        response: this.translate.instant('voiceAssistant.workflow.clarify'),
        speak: true,
      };
    }
    const slots = (this.merged.shared.slots ?? {}) as Record<
      string,
      { assistantPromptKey?: string }
    >;
    const promptKey =
      slots[slotKey]?.assistantPromptKey ?? 'voiceAssistant.workflow.clarify';
    const chips = this.buildChipsForSlot(slotKey);
    this.presentGuidedTurnWithChips(promptKey, {}, chips);
    return {
      response: this.buildLastAssistantMessage(),
      speak: true,
    };
  }

  private finishTurnAfterPresent(): WorkflowTurnResult {
    const fu = this.takePendingFollowUp();
    return {
      response: this.buildLastAssistantMessage(),
      speak: true,
      followUp$: fu ?? undefined,
    };
  }

  private takePendingFollowUp(): Observable<WorkflowTurnResult> | null {
    const p = this.pendingFollowUp;
    this.pendingFollowUp = null;
    return p;
  }

  private buildLastAssistantMessage(): string {
    const guided = this.conv.lastGuidedDisplayText();
    if (guided?.trim()) return guided;
    const key = this.conv.lastGuidedMessageKey();
    if (!key) return '';
    return this.translate.instant(key, this.conv.lastGuidedMessageParams());
  }

  /** Composes question + natural option list for TTS and hybrid panel (chips from config). */
  private composeGuidedDisplayBody(
    messageKey: string,
    params: Record<string, string>,
    chips: WorkflowChipOption[]
  ): string | null {
    const lead = this.translate.instant(messageKey, params);
    if (!chips?.length) return null;
    const labels = chips.map(c => String(c.label ?? '').trim()).filter(Boolean);
    if (!labels.length) return null;
    const maxSpoken = 5;
    const truncated = labels.length > maxSpoken;
    const spoken = truncated ? labels.slice(0, maxSpoken) : [...labels];
    const namesSpeech = this.formatSpokenOptionList(spoken);
    const tail = truncated
      ? this.translate.instant('voiceAssistant.workflow.optionSpeechTruncated', { names: namesSpeech })
      : this.translate.instant('voiceAssistant.workflow.optionSpeechFull', { names: namesSpeech });
    return `${lead}\n\n${tail}`;
  }

  private formatSpokenOptionList(labels: readonly string[]): string {
    if (labels.length === 0) return '';
    if (labels.length === 1) return labels[0]!;
    const orWord = this.translate.instant('voiceAssistant.workflow.optionOr');
    if (labels.length === 2) return `${labels[0]} ${orWord} ${labels[1]}`;
    const init = labels.slice(0, -1).join(', ');
    const last = labels[labels.length - 1]!;
    return `${init}, ${orWord} ${last}`;
  }

  private presentGuidedTurnWithChips(
    messageKey: string,
    params: Record<string, string>,
    chips: WorkflowChipOption[]
  ): void {
    const display = this.composeGuidedDisplayBody(messageKey, params, chips);
    this.conv.presentGuidedTurn(messageKey, params, chips, display);
  }

  private presentEntryOrNext(): void {
    if (!this.merged) return;
    const entryFlow = this.merged.artifact.entryFlow;
    const ask = entryFlow?.askGenerationMode !== false;
    if (
      ask &&
      this.conv.generationPathMode() === 'unset' &&
      !this.conv.isEducationalHybridActive()
    ) {
      this.presentGenerationPathChoice();
      return;
    }
    if (this.conv.generationPathMode() === 'unset' && this.conv.isEducationalHybridActive()) {
      this.conv.generationPathMode.set('custom');
    }
    this.presentNextSlot();
  }

  private presentGenerationPathChoice(): void {
    this.conv.currentSlotKey.set(WORKFLOW_SLOT_GENERATION_PATH);
    const chips: WorkflowChipOption[] = [
      {
        label: this.translate.instant('voiceAssistant.workflow.pathGeneric'),
        value: 'quick',
        slotKey: WORKFLOW_SLOT_GENERATION_PATH,
      },
      {
        label: this.translate.instant('voiceAssistant.workflow.pathCustom'),
        value: 'custom',
        slotKey: WORKFLOW_SLOT_GENERATION_PATH,
      },
    ];
    this.presentGuidedTurnWithChips('voiceAssistant.workflow.entryFlowPickPath', {}, chips);
  }

  private applyGenerationPathChip(encoded: string): WorkflowTurnResult {
    const pick = encoded === 'generic' ? 'quick' : encoded;
    if (pick === 'quick') {
      return this.startQuickFlow();
    }
    if (pick === 'custom') {
      this.conv.generationPathMode.set('custom');
      this.conv.workflowMode.set('guided');
      const art = this.conv.sessionArtifact();
      this.logDebug('[workflow] custom path selected', { artifact: art, mergedArtifact: this.merged?.artifactKey });
      if (art) {
        this.conv.requestHybridForArtifact(art);
      }
      if (!this.lastDomains().length) {
        return {
          response: this.translate.instant('voiceAssistant.workflow.loadingOptions'),
          speak: true,
          followUp$: this.loadDomainsForCustomPath$(),
        };
      }
      this.presentNextSlot();
      return this.finishTurnAfterPresent();
    }
    this.logClarifyFallback('invalid_generation_path_encoded', encoded);
    return {
      response: this.translate.instant('voiceAssistant.workflow.clarify'),
      speak: true,
    };
  }

  private startQuickFlow(inlineTheme?: string): WorkflowTurnResult {
    this.conv.generationPathMode.set('quick');
    this.conv.workflowMode.set('quick');
    const theme = inlineTheme?.trim() || null;
    if (theme) {
      this.conv.quickPrompt.set(theme);
      return this.beginQuickGenerateTurn();
    }
    this.presentQuickThemeTurn();
    return this.finishTurnAfterPresent();
  }

  private presentQuickThemeTurn(): void {
    const artifact = this.conv.sessionArtifact() ?? 'resource';
    this.conv.currentSlotKey.set(WORKFLOW_SLOT_QUICK_THEME);
    const chips: WorkflowChipOption[] = [
      {
        label: this.translate.instant('voiceAssistant.workflow.quickAge34'),
        value: 'ages 3–4',
        slotKey: WORKFLOW_SLOT_QUICK_AGE,
      },
      {
        label: this.translate.instant('voiceAssistant.workflow.quickAge45'),
        value: 'ages 4–5',
        slotKey: WORKFLOW_SLOT_QUICK_AGE,
      },
      {
        label: this.translate.instant('voiceAssistant.workflow.quickAge56'),
        value: 'ages 5–6',
        slotKey: WORKFLOW_SLOT_QUICK_AGE,
      },
    ];
    this.presentGuidedTurnWithChips(
      'voiceAssistant.workflow.askQuickTheme',
      { artifact },
      chips
    );
  }

  private applyQuickChipSelection(slotKey: string, encodedValue: string): WorkflowTurnResult {
    if (slotKey === WORKFLOW_SLOT_QUICK_AGE) {
      this.conv.quickAgeHint.set(encodedValue);
      if (this.conv.quickPrompt()?.trim()) {
        return this.beginQuickGenerateTurn();
      }
      this.presentQuickThemeTurn();
      return this.finishTurnAfterPresent();
    }
    if (slotKey === WORKFLOW_SLOT_QUICK_THEME) {
      this.conv.quickPrompt.set(encodedValue);
      return this.beginQuickGenerateTurn();
    }
    this.logClarifyFallback('quick_chip_slot_unrecognized', encodedValue, slotKey);
    return {
      response: this.translate.instant('voiceAssistant.workflow.clarify'),
      speak: true,
    };
  }

  private handleQuickUserText(text: string): WorkflowTurnResult {
    if (this.conv.isSessionTimedOut()) {
      this.conv.presentClarification('voiceAssistant.workflow.sessionTimeout');
      return {
        response: this.translate.instant('voiceAssistant.workflow.sessionTimeout'),
        speak: true,
      };
    }

    const trimmed = text.trim();
    if (!trimmed) {
      this.logClarifyFallback('quick_empty_input', text);
      return {
        response: this.translate.instant('voiceAssistant.workflow.clarify'),
        speak: true,
      };
    }

    const current = this.conv.currentSlotKey();
    if (current === WORKFLOW_SLOT_QUICK_THEME || !this.conv.quickPrompt()) {
      const theme = this.extractQuickTheme(trimmed) ?? trimmed;
      this.conv.quickPrompt.set(theme);
      return this.beginQuickGenerateTurn();
    }

    return this.beginQuickGenerateTurn();
  }

  private beginQuickGenerateTurn(): WorkflowTurnResult {
    this.conv.currentSlotKey.set(null);
    this.presentGuidedTurnWithChips('voiceAssistant.workflow.quickGenerating', {}, []);
    return {
      response: this.buildLastAssistantMessage(),
      speak: false,
      followUp$: this.executeQuickGeneration$(),
    };
  }

  private extractQuickTheme(text: string): string | null {
    const t = text.trim();
    if (!t) return null;
    const about = t.match(/\b(?:about|on|for)\s+(.+)/i);
    if (about?.[1]) return about[1].trim();
    return t;
  }

  private loadDomainsForCustomPath$(): Observable<WorkflowTurnResult> {
    if (!this.merged) {
      return of({
        response: this.translate.instant('voiceAssistant.workflow.sessionEnded'),
        speak: true,
      });
    }
    const lang =
      typeof window !== 'undefined' ? localStorage.getItem('targetedLang') || 'en' : 'en';
    const assessmentId = Number(
      (this.merged.shared as { apiConstants?: { assessmentIdForDomains?: number } })?.apiConstants
        ?.assessmentIdForDomains ?? 14
    );
    return this.va.getDomains(assessmentId, lang).pipe(
      map((res: unknown) => {
        const data = (res as { data?: DomainRow[] })?.data ?? [];
        this.lastDomains.set(Array.isArray(data) ? data : []);
        this.presentNextSlot();
        return this.finishTurnAfterPresent();
      }),
      catchError(() => {
        this.lastDomains.set([]);
        this.presentNextSlot();
        return of(this.finishTurnAfterPresent());
      })
    );
  }

  private executeQuickGeneration$(): Observable<WorkflowTurnResult> {
    const artifact = this.conv.sessionArtifact() ?? this.conv.activeArtifact();
    const prompt = this.conv.quickPrompt()?.trim();
    if (!artifact || !prompt) {
      this.presentQuickThemeTurn();
      return of(this.finishTurnAfterPresent());
    }
    const lang =
      typeof window !== 'undefined' ? localStorage.getItem('targetedLang') || 'en' : 'en';
    const ageHint = this.conv.quickAgeHint() ?? undefined;
    return this.va
      .generateQuickContent({
        artifact,
        prompt,
        language: lang,
        ageHint,
      })
      .pipe(
        map((res: unknown) => {
          const body = (res as { data?: QuickContentResult })?.data ?? (res as QuickContentResult);
          const title = String((body as QuickContentResult)?.title ?? '').trim();
          const content = String((body as QuickContentResult)?.content ?? '').trim();
          const notes = String((body as QuickContentResult)?.notes ?? '').trim();
          const quick = { title, content, notes: notes || undefined };
          if (title && content) {
            this.conv.setQuickResultWithChat(quick);
          }
          const plainContent = formatQuickContentPlainText(quick);
          this.endSession();
          this.conv.resetEducationalSession();
          return { response: plainContent, speak: true };
        }),
        catchError(() => {
          this.endSession();
          this.conv.resetEducationalSession();
          return of({
            response: this.translate.instant('voiceAssistant.workflow.generateFailed'),
            speak: true,
          });
        })
      );
  }


  /** Persist slot into snapshot only (no HTTP / presentNextSlot). */
  private applyEncodedValue(slotKey: string, encoded: string): boolean {
    if (slotKey === 'topicDomain' && encoded.startsWith('id:')) {
      const id = Number(encoded.slice(3));
      const d = this.lastDomains().find(x => x.id === id);
      if (!d) return false;
      this.conv.applyOrchestratorPatch({ topicDomain: { id: d.id, name: d.name } });
      return true;
    }
    if (slotKey === 'learningFocus' && encoded.startsWith('id:')) {
      const id = Number(encoded.slice(3));
      const q = this.lastQuestions().find(x => x.id === id);
      if (!q) return false;
      this.conv.applyOrchestratorPatch({
        learningFocus: { id: q.id, content: String(q.content ?? '') },
      });
      return true;
    }
    if (slotKey === 'pedagogy') {
      this.conv.applyOrchestratorPatch({ pedagogy: encoded });
      return true;
    }
    if (slotKey === 'standardLevel') {
      this.conv.applyOrchestratorPatch({ standardLevel: encoded });
      return true;
    }
    if (slotKey === 'countryRegion') {
      this.conv.applyOrchestratorPatch({ countryRegion: encoded });
      return true;
    }
    return false;
  }

  private presentNextSlot(): void {
    if (!this.merged?.shared) {
      console.error('[workflow] presentNextSlot skipped: no merged.shared', {
        sessionArtifact: this.conv.sessionArtifact(),
      });
      return;
    }
    const workflows = (this.merged.shared.workflows ?? {}) as { guided?: { slotOrder?: string[] } };
    const order = (workflows.guided?.slotOrder ?? []) as string[];
    const snap = this.conv.snapshot();
    const slots = (this.merged.shared.slots ?? {}) as Record<
      string,
      { required?: boolean; assistantPromptKey?: string; options?: SlotOptionJson[] }
    >;

    const isMissing = (key: string): boolean => {
      switch (key) {
        case 'topicDomain':
          return !snap.topicDomain;
        case 'learningFocus':
          return !snap.learningFocus;
        case 'pedagogy':
          return !snap.pedagogy;
        case 'standardLevel':
          return !snap.standardLevel || !this.conv.isWorkflowSlotConfirmed('standardLevel');
        case 'countryRegion':
          return (
            snap.countryRegion == null ||
            snap.countryRegion === '' ||
            !this.conv.isWorkflowSlotConfirmed('countryRegion')
          );
        default:
          return false;
      }
    };

    const next = order.find(k => isMissing(k)) ?? null;
    this.logDebug('[workflow] presentNextSlot', {
      artifact: this.merged.artifactKey,
      generationMode: this.conv.generationPathMode(),
      nextSlot: next,
      domainCount: this.lastDomains().length,
    });
    this.conv.logActiveWorkflowDebug(next);

    if (!next) {
      const allRequiredFilled =
        snap.topicDomain &&
        snap.learningFocus &&
        snap.pedagogy &&
        snap.standardLevel &&
        this.conv.isWorkflowSlotConfirmed('countryRegion') &&
        this.conv.isWorkflowSlotConfirmed('standardLevel');
      if (allRequiredFilled) {
        logAssistantDebug('WORKFLOW_COMPLETE', { artifact: this.merged.artifactKey });
        this.conv.currentSlotKey.set(null);
        this.pendingFollowUp = this.executeGeneration$();
        this.presentGuidedTurnWithChips('voiceAssistant.workflow.generating', {}, []);
        return;
      }
    }

    this.conv.currentSlotKey.set(next);

    if (!next) {
      this.presentGuidedTurnWithChips('voiceAssistant.workflow.clarify', {}, []);
      return;
    }

    const promptKey = slots[next]?.assistantPromptKey ?? 'voiceAssistant.workflow.clarify';
    const chips = this.buildChipsForSlot(next);
    this.presentGuidedTurnWithChips(promptKey, {}, chips);
  }

  private buildChipsForSlot(slotKey: string): WorkflowChipOption[] {
    const domains = dedupeById(this.lastDomains());
    const questions = dedupeById(this.lastQuestions());
    if (slotKey === 'topicDomain') {
      return domains.slice(0, 10).map(d => ({
        label: String(d.name ?? `Domain ${d.id}`),
        value: `id:${d.id}`,
        slotKey: 'topicDomain',
      }));
    }
    if (slotKey === 'learningFocus') {
      return questions.slice(0, 12).map(q => {
        const content = String(q.content ?? '');
        const short = content.length > 48 ? content.slice(0, 45) + '…' : content;
        return {
          label: short || `Question ${q.id}`,
          value: `id:${q.id}`,
          slotKey: 'learningFocus',
        };
      });
    }
    const slotMap = (this.merged?.shared?.slots ?? {}) as
      | Record<string, { options?: SlotOptionJson[] }>
      | undefined;
    const opts = slotMap?.[slotKey]?.options?.filter(o => o.value !== '' && !(o as { disabled?: boolean }).disabled) ?? [];
    return opts.slice(0, 12).map(o => ({
      label: o.label,
      value: o.value,
      slotKey,
    }));
  }

  private executeGeneration$(): Observable<WorkflowTurnResult> {
    const artifact = this.conv.sessionArtifact() ?? this.conv.activeArtifact();
    const snap = this.conv.snapshot();
    const { teacherId, schoolId } = this.readContextIds();
    const built = this.payloadBuilder.buildGeneratePayload(snap, artifact ?? '', teacherId, schoolId);
    if (!built) {
      return of({
        response: this.translate.instant('voiceAssistant.workflow.clarify'),
        speak: true,
      });
    }
    const payload = {
      teacherId: built.teacherId ?? undefined,
      schoolId: built.schoolId ?? undefined,
      domainName: built.domainName,
      questionContent: built.questionContent,
      pedagogy: built.pedagogy,
      level: built.level,
      artifact: built.artifact,
      numberOfSuggestions: built.numberOfSuggestions,
      country: built.country,
      language: built.language,
      includeLocalContext: built.includeLocalContext,
    };
    return this.va.generateEducationalContent(payload).pipe(
      map((res: unknown) => {
        const intro = this.translate.instant('voiceAssistant.workflow.generateSuccess');
        this.endSession();
        this.conv.resetEducationalSession();
        const parsed = parseEducationalContentResponse(res, intro, {
          artifact: built.artifact,
        });
        if (parsed.view) {
          this.conv.setGeneratedEducationalViewWithChat(parsed.view, {
            artifactKey: built.artifact,
          });
        }
        const plainContent = parsed.view
          ? finalizeAssistantEducationalMessage(parsed.view, intro).content
          : parsed.displayText;
        return {
          response: plainContent,
          speak: true,
        };
      }),
      catchError(() => {
        this.endSession();
        this.conv.resetEducationalSession();
        return of({
          response: this.translate.instant('voiceAssistant.workflow.generateFailed'),
          speak: true,
        });
      })
    );
  }

  private logDebug(message: string, data?: unknown): void {
    if (!environment.production) {
      if (data !== undefined) {
        console.info(message, data);
      } else {
        console.info(message);
      }
    }
  }

  private readContextIds(): { teacherId: number | null; schoolId: number | null } {
    if (typeof window === 'undefined' || !window.localStorage) {
      return { teacherId: null, schoolId: null };
    }
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return {
        teacherId: typeof u.teacher_id === 'number' ? u.teacher_id : null,
        schoolId: typeof u.school_id === 'number' ? u.school_id : null,
      };
    } catch {
      return { teacherId: null, schoolId: null };
    }
  }

  private isAllowedChipSlot(slotKey: string): boolean {
    if (
      slotKey === WORKFLOW_SLOT_GENERATION_PATH ||
      slotKey === WORKFLOW_SLOT_QUICK_THEME ||
      slotKey === WORKFLOW_SLOT_QUICK_AGE
    ) {
      return true;
    }
    return this.conv.currentSlotKey() === slotKey;
  }

  private logClarifyFallback(
    reason: string,
    userInput?: string,
    matchedOption?: string | null
  ): void {
    const debug = this.conv.getActiveWorkflowDebugState();
    logWorkflowFallback({
      reason,
      userInput,
      workflowId: debug.workflowId,
      currentStep: debug.currentStep,
      workflowStatus: debug.workflowStatus,
      matchedOption: matchedOption ?? null,
      workflowData: debug.workflowData,
    });
  }

  private presentStaleChipTurn(slotKey: string, encodedValue: string): WorkflowTurnResult {
    logWorkflowError({
      workflowId: this.conv.sessionArtifact(),
      currentStep: this.conv.currentSlotKey(),
      userInput: encodedValue,
      workflowData: this.conv.snapshot(),
      error: new Error(
        `Stale chip: expected slot ${this.conv.currentSlotKey() ?? 'none'}, got ${slotKey}`
      ),
    });
    const current = this.conv.currentSlotKey();
    if (current) {
      return this.presentInvalidSlotClarificationTurn(current);
    }
    this.logClarifyFallback('stale_chip_no_current_slot', encodedValue, slotKey);
    this.conv.presentClarification('voiceAssistant.workflow.clarify');
    return {
      response: this.translate.instant('voiceAssistant.workflow.clarify'),
      speak: true,
    };
  }

}
